import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X, ArrowRight, ArrowLeft, Loader2, LayoutTemplate, Search, BookOpen,
  CheckCircle2, FileText, Clock, Bookmark, Hash, ChevronLeft, ChevronRight, Trash2, Eye, SlidersHorizontal, ChevronDown,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  useLayouts, useSavedArticlesPreview, useRecentlyRead,
  useArticleSearch, useCreatePage,
} from '../api/hooks'
import { httpArticleService } from '../api/http/articleService'
import { LayoutPreview } from '../components/layout-creator/LayoutPreview'
import { PageSlotRenderer } from '../components/page-renderer/PageSlotRenderer'
import { DarkModeToggle } from '../components/DarkModeToggle'
import { PAGE_BLOCK_REGISTRY } from '../components/page-blocks/registry'
import { EDITORIAL_GRID_PRESET_CONFIG } from '../components/page-blocks/definitions/editorialGrid'
import type { EditorialGridLayoutPreset } from '../components/page-blocks/definitions/editorialGrid'
import type { Layout, LayoutBlock, PageBlockInput, PageBlockType, ColumnBlock, PageBlockColumnKey } from '../api/types'
import type { WireArticle } from '../api/types'
import type { CanvasSlot } from '../components/editor-shared/canvasTypes'
import type { ArticleSearchFilters } from '../api/services/articleService'
import { useTopics, useRegions } from '../hooks/useTaxonomy'
import { TaxonomyPicker } from '../components/common/TaxonomyPicker'

// ── Article capacity ──────────────────────────────────────────────────────────

const UNBOUNDED_CAP = 5

type BlockCapacity = { label: string; min: number; max: number; capped: boolean }
type LayoutCapacity = { totalMin: number; totalMax: number; blocks: BlockCapacity[] }

function blockCap(def: { label: string; minArticles: number; maxArticles: number }): BlockCapacity {
  const capped = def.maxArticles > UNBOUNDED_CAP
  return {
    label: def.label,
    min: capped ? 1 : def.minArticles,
    max: capped ? UNBOUNDED_CAP : def.maxArticles,
    capped,
  }
}

function computeLayoutCapacity(blocks: LayoutBlock[]): LayoutCapacity {
  const COL_KEYS = ['column1', 'column2', 'column3'] as const
  let totalMin = 0
  let totalMax = 0
  const perBlock: BlockCapacity[] = []
  for (const block of blocks) {
    if (block.type === 'editorial-grid') {
      for (const colKey of COL_KEYS) {
        const colBlocks = block.columnBlocks?.[colKey] ?? []
        for (const cb of colBlocks) {
          const def = PAGE_BLOCK_REGISTRY[cb.type]
          if (!def || def.maxArticles === 0) continue
          const cap = blockCap(def)
          totalMin += cap.min; totalMax += cap.max; perBlock.push(cap)
        }
      }
    } else {
      const def = PAGE_BLOCK_REGISTRY[block.type]
      if (!def || def.maxArticles === 0) continue
      const cap = blockCap(def)
      totalMin += cap.min; totalMax += cap.max; perBlock.push(cap)
    }
  }
  return { totalMin, totalMax, blocks: perBlock }
}

function fmtCapacity(min: number, max: number, capped: boolean): string {
  if (min === max) return `${min}`
  if (capped) return `${min}–${max}+`
  return `${min}–${max}`
}

/**
 * Derive config overrides from the actual article count so the preview and
 * the saved page reflect reality rather than stale defaults.
 *
 * story-row    — columnCount tracks article count (clamped to 2–5)
 * hero-overlay — showRightOverlay is false when only 1 article is selected
 *                (no secondary slots to fill, so the right panel stays hidden)
 */
function deriveBlockConfig(
  type: PageBlockType,
  config: Record<string, string | number | boolean | null>,
  articleCount: number,
): Record<string, string | number | boolean | null> {
  switch (type) {
    case 'story-row':
      return { ...config, columnCount: Math.max(2, Math.min(5, articleCount)) }
    case 'hero-overlay':
      return { ...config, showRightOverlay: articleCount > 1 }
    default:
      return config
  }
}

// ── Wizard steps ──────────────────────────────────────────────────────────────

const STEPS = [
  { id: 'layout',   label: 'Choose Layout'  },
  { id: 'articles', label: 'Add Articles'   },
  { id: 'details',  label: 'Book Details'   },
] as const

type WizardStep = (typeof STEPS)[number]['id']

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: WizardStep }) {
  const currentIndex = STEPS.findIndex(s => s.id === current)
  return (
    <nav aria-label="Wizard steps" className="flex items-center">
      {STEPS.map((step, i) => {
        const isActive = step.id === current
        const isDone   = i < currentIndex
        const isFuture = i > currentIndex
        return (
          <div key={step.id} className="flex items-center">
            {i > 0 && (
              <div className={`mx-3 h-px w-8 transition-colors ${isDone ? 'bg-accent-primary-solid' : 'bg-line'}`} aria-hidden="true" />
            )}
            <div className="flex items-center gap-2">
              <div
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold tabular-nums transition-colors ${
                  isDone    ? 'bg-accent-primary-solid text-white'
                  : isActive ? 'bg-accent-primary-solid text-white ring-2 ring-accent-primary-solid ring-offset-2 ring-offset-canvas'
                  : 'bg-fill text-fg-secondary'
                }`}
                aria-current={isActive ? 'step' : undefined}
              >
                {isDone ? <CheckCircle2 size={12} aria-hidden="true" /> : i + 1}
              </div>
              <span className={`text-[11px] font-semibold transition-colors ${
                isActive ? 'text-fg' : isFuture ? 'text-fg-secondary opacity-35' : 'text-fg-secondary'
              }`}>
                {step.label}
              </span>
            </div>
          </div>
        )
      })}
    </nav>
  )
}

// ── Wizard bar ────────────────────────────────────────────────────────────────

function WizardBar({
  step,
  nextLabel,
  nextDisabled,
  onBack,
  onCancel,
  onNext,
  nextLoading,
  onPreview,
}: {
  step: WizardStep
  nextLabel: string
  nextDisabled?: boolean
  onBack?: () => void
  onCancel: () => void
  onNext: () => void
  nextLoading?: boolean
  onPreview?: () => void
}) {
  return (
    <div className="flex shrink-0 items-center justify-between border-b border-line bg-canvas px-5 py-3 shadow-sm">
      <div className="flex items-center gap-2">
        {onBack ? (
          <button type="button" onClick={onBack}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg">
            <ArrowLeft size={12} aria-hidden="true" />
            Back
          </button>
        ) : (
          <button type="button" onClick={onCancel}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg">
            <X size={12} aria-hidden="true" />
            Cancel
          </button>
        )}
      </div>
      <StepIndicator current={step} />
      <div className="flex items-center gap-2">
        {onPreview && (
          <button type="button" onClick={onPreview}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg">
            <Eye size={12} aria-hidden="true" />
            Preview
          </button>
        )}
        <DarkModeToggle className="flex h-7 w-7 items-center justify-center rounded border border-line bg-canvas text-fg transition-colors hover:bg-fill" />
        <button type="button" onClick={onNext} disabled={nextDisabled || nextLoading}
          className="flex items-center gap-1.5 rounded bg-accent-primary-solid px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-accent-primary-hover disabled:cursor-not-allowed disabled:opacity-40">
          {nextLoading && <Loader2 size={11} className="animate-spin" aria-hidden="true" />}
          {nextLabel}
          {!nextLoading && <ArrowRight size={12} aria-hidden="true" />}
        </button>
      </div>
    </div>
  )
}

// ── Shared: article card ──────────────────────────────────────────────────────

function ArticleCard({
  article,
  selected,
  disabled,
  onToggle,
}: {
  article: WireArticle
  selected: boolean
  disabled: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled && !selected}
      aria-pressed={selected}
      className={`relative w-full overflow-hidden text-left transition-colors ${
        selected   ? 'bg-accent-primary-surface'
        : disabled ? 'opacity-40 cursor-not-allowed'
        : 'hover:bg-fill/60'
      }`}
    >
      <div className={`absolute inset-y-0 left-0 w-[3px] ${selected ? 'bg-accent-primary-solid' : 'bg-transparent'}`} aria-hidden="true" />
      <div className="py-2.5 pl-5 pr-3">
        <div className="flex items-start justify-between gap-2">
          <p className={`line-clamp-2 text-[12px] font-semibold leading-snug ${selected ? 'text-accent-primary-fg' : 'text-fg-secondary'}`}>
            {article.title}
          </p>
          {selected && <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-accent-primary-solid" aria-hidden="true" />}
        </div>
        {article.documentId && (
          <p className="mt-0.5 font-mono text-[10px] text-fg-secondary opacity-50">{article.documentId}</p>
        )}
      </div>
    </button>
  )
}

// ── Article sources ───────────────────────────────────────────────────────────

type ArticleSource = 'saved' | 'recent' | 'search' | 'by-id'

function ArticleListShell({
  articles,
  isLoading,
  emptyMsg,
  selectedIds,
  atMax,
  onToggle,
}: {
  articles: WireArticle[]
  isLoading: boolean
  emptyMsg: string
  selectedIds: string[]
  atMax: boolean
  onToggle: (a: WireArticle) => void
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-fg-secondary">
        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
      </div>
    )
  }
  if (articles.length === 0) {
    return <p className="px-4 py-8 text-center text-[11px] text-fg-secondary opacity-50">{emptyMsg}</p>
  }
  return (
    <div className="flex-1 overflow-y-auto">
      {articles.map(a => (
        <ArticleCard
          key={a.id}
          article={a}
          selected={selectedIds.includes(a.id)}
          disabled={atMax && !selectedIds.includes(a.id)}
          onToggle={() => onToggle(a)}
        />
      ))}
    </div>
  )
}

function SavedSource({ selectedIds, atMax, onToggle }: { selectedIds: string[]; atMax: boolean; onToggle: (a: WireArticle) => void }) {
  const { data, isLoading } = useSavedArticlesPreview(50)
  return (
    <ArticleListShell
      articles={data?.items ?? []}
      isLoading={isLoading}
      emptyMsg="No saved articles yet."
      selectedIds={selectedIds}
      atMax={atMax}
      onToggle={onToggle}
    />
  )
}

function RecentSource({ selectedIds, atMax, onToggle }: { selectedIds: string[]; atMax: boolean; onToggle: (a: WireArticle) => void }) {
  const { data, isLoading } = useRecentlyRead(30)
  return (
    <ArticleListShell
      articles={data?.items ?? []}
      isLoading={isLoading}
      emptyMsg="No recently read articles."
      selectedIds={selectedIds}
      atMax={atMax}
      onToggle={onToggle}
    />
  )
}

type SearchFilterDraft = {
  fromDate: string
  toDate: string
  regions: string[]
  topics: string[]
  productTypes: string[]
  offices: string[]
}

const SEARCH_PRODUCT_TYPE_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'biographic', label: 'Biographic' },
] as const

function countActiveSearchFilters(d: SearchFilterDraft): number {
  return [
    d.fromDate || d.toDate,
    d.regions.length > 0,
    d.topics.length > 0,
    d.productTypes.length > 0,
    d.offices.length > 0,
  ].filter(Boolean).length
}

function buildSearchFilters(d: SearchFilterDraft): ArticleSearchFilters {
  return {
    ...(d.fromDate && { fromDate: `${d.fromDate}T00:00:00Z` }),
    ...(d.toDate && { toDate: `${d.toDate}T23:59:59Z` }),
    ...(d.regions.length > 0 && { regionsAndCountries: d.regions }),
    ...(d.topics.length > 0 && { issuesAndTopics: d.topics }),
    ...(d.productTypes.length > 0 && { reportingAndProductTypes: d.productTypes }),
    ...(d.offices.length > 0 && { producingOffices: d.offices }),
  }
}

function SearchTagInput({
  values,
  onChange,
}: {
  values: string[]
  onChange: (next: string[]) => void
}) {
  const [text, setText] = useState('')

  function commit() {
    const trimmed = text.trim()
    if (trimmed && !values.includes(trimmed)) onChange([...values, trimmed])
    setText('')
  }

  return (
    <div className="flex min-h-[32px] flex-wrap items-center gap-1 rounded border border-line bg-canvas px-2 py-1 focus-within:ring-1 focus-within:ring-line-strong">
      {values.map(v => (
        <span key={v} className="flex items-center gap-0.5 rounded-full border border-line bg-fill px-1.5 py-0.5 text-[10px] text-fg">
          {v}
          <button
            type="button"
            onClick={() => onChange(values.filter(x => x !== v))}
            className="text-fg-secondary transition-colors hover:text-fg"
            aria-label={`Remove ${v}`}
          >
            <X size={9} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit() }
          if (e.key === 'Backspace' && !text && values.length) onChange(values.slice(0, -1))
        }}
        onBlur={commit}
        placeholder={values.length === 0 ? 'Add office and press Enter…' : ''}
        className="min-w-[110px] flex-1 bg-transparent text-[11px] text-fg placeholder:text-fg-secondary outline-none"
      />
    </div>
  )
}

function SearchSource({ selectedIds, atMax, onToggle }: { selectedIds: string[]; atMax: boolean; onToggle: (a: WireArticle) => void }) {
  const [query, setQuery] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filterDraft, setFilterDraft] = useState<SearchFilterDraft>({
    fromDate: '',
    toDate: '',
    regions: [],
    topics: [],
    productTypes: [],
    offices: [],
  })
  const { data: topicOptions = [], isLoading: topicsLoading } = useTopics()
  const { data: regionOptions = [], isLoading: regionsLoading } = useRegions()

  const activeFilterCount = countActiveSearchFilters(filterDraft)
  const hasFilters = activeFilterCount > 0
  const filters = buildSearchFilters(filterDraft)

  const { data, isLoading } = useArticleSearch(query, hasFilters ? filters : undefined)

  function clearFilters() {
    setFilterDraft({
      fromDate: '',
      toDate: '',
      regions: [],
      topics: [],
      productTypes: [],
      offices: [],
    })
  }

  function toggleProductType(value: string) {
    setFilterDraft((d) => ({
      ...d,
      productTypes: d.productTypes.includes(value)
        ? d.productTypes.filter((x) => x !== value)
        : [...d.productTypes, value],
    }))
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="shrink-0 border-b border-line px-3 py-2 space-y-2">
        <div className="relative">
          <Search size={11} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-secondary" aria-hidden="true" />
          <input
            type="search"
            autoFocus
            placeholder="Search by title…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="h-7 w-full rounded border border-line bg-canvas pl-7 pr-3 text-xs text-fg placeholder:text-fg-secondary focus:outline-none focus:ring-1 focus:ring-line-strong"
          />
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen(o => !o)}
            className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-fg-secondary transition-colors hover:text-fg"
          >
            <SlidersHorizontal size={11} aria-hidden="true" />
            Advanced
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-violet-9 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown
              size={11}
              aria-hidden="true"
              className={`transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[10px] font-semibold uppercase tracking-wider text-fg-secondary transition-colors hover:text-fg"
            >
              Clear
            </button>
          )}
        </div>

        {filtersOpen && (
          <div className="space-y-3 rounded border border-line bg-surface p-2.5">
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-fg-secondary">From</span>
                <input
                  type="date"
                  value={filterDraft.fromDate}
                  onChange={(e) => setFilterDraft((d) => ({ ...d, fromDate: e.target.value }))}
                  className="h-7 rounded border border-line bg-canvas px-2 text-[11px] text-fg outline-none focus:ring-1 focus:ring-line-strong [color-scheme:light] dark:[color-scheme:dark]"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-fg-secondary">To</span>
                <input
                  type="date"
                  value={filterDraft.toDate}
                  onChange={(e) => setFilterDraft((d) => ({ ...d, toDate: e.target.value }))}
                  className="h-7 rounded border border-line bg-canvas px-2 text-[11px] text-fg outline-none focus:ring-1 focus:ring-line-strong [color-scheme:light] dark:[color-scheme:dark]"
                />
              </label>
            </div>

            <TaxonomyPicker
              label="Issues & Topics"
              options={topicOptions}
              selected={filterDraft.topics}
              onChange={(v) => setFilterDraft((d) => ({ ...d, topics: v }))}
              placeholder="No topics selected"
              loading={topicsLoading}
            />

            <TaxonomyPicker
              label="Regions & Countries"
              options={regionOptions}
              selected={filterDraft.regions}
              onChange={(v) => setFilterDraft((d) => ({ ...d, regions: v }))}
              placeholder="No regions selected"
              loading={regionsLoading}
            />

            <div>
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Producing Office</p>
              <SearchTagInput
                values={filterDraft.offices}
                onChange={(v) => setFilterDraft((d) => ({ ...d, offices: v }))}
              />
            </div>

            <div>
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Product Type</p>
              <div className="flex flex-wrap gap-1.5">
                {SEARCH_PRODUCT_TYPE_OPTIONS.map(({ value, label }) => {
                  const active = filterDraft.productTypes.includes(value)
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleProductType(value)}
                      className={`rounded border px-2 py-1 text-[10px] font-semibold transition-colors ${
                        active
                          ? 'border-violet-7 bg-violet-3 text-violet-11'
                          : 'border-line bg-canvas text-fg-secondary hover:border-line-strong hover:text-fg'
                      }`}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      <ArticleListShell
        articles={data ?? []}
        isLoading={isLoading && (query.length >= 2 || hasFilters)}
        emptyMsg={query.length < 2 && !hasFilters ? 'Type at least 2 characters or use filters.' : 'No results.'}
        selectedIds={selectedIds}
        atMax={atMax}
        onToggle={onToggle}
      />
    </div>
  )
}

function ByIdSource({ selectedIds, atMax, onToggle }: { selectedIds: string[]; atMax: boolean; onToggle: (a: WireArticle) => void }) {
  const [id, setId]       = useState('')
  const [result, setResult] = useState<WireArticle | null>(null)
  const [error, setError]   = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  async function lookup() {
    const trimmed = id.trim()
    if (!trimmed) return
    setLooking(true); setError(null); setResult(null)
    try {
      const article = await httpArticleService.getPublished(trimmed) as unknown as WireArticle
      setResult(article)
    } catch {
      setError('Article not found or you do not have access.')
    } finally {
      setLooking(false)
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden px-3 py-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Hash size={11} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-secondary" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            autoFocus
            placeholder="Paste article ID…"
            value={id}
            onChange={e => { setId(e.target.value); setError(null); setResult(null) }}
            onKeyDown={e => { if (e.key === 'Enter') void lookup() }}
            className="h-7 w-full rounded border border-line bg-canvas pl-7 pr-3 text-xs text-fg placeholder:text-fg-secondary focus:outline-none focus:ring-1 focus:ring-line-strong"
          />
        </div>
        <button type="button" onClick={() => void lookup()} disabled={!id.trim() || looking}
          className="flex items-center gap-1 rounded border border-line bg-canvas px-3 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill disabled:opacity-40">
          {looking ? <Loader2 size={11} className="animate-spin" /> : 'Look up'}
        </button>
      </div>
      {error && <p className="text-[11px] text-error-fg">{error}</p>}
      {result && (
        <ArticleCard
          article={result}
          selected={selectedIds.includes(result.id)}
          disabled={atMax && !selectedIds.includes(result.id)}
          onToggle={() => onToggle(result)}
        />
      )}
    </div>
  )
}

// ── Article picker (left shelf for articles step) ─────────────────────────────

const SOURCE_TABS: { id: ArticleSource; label: string; icon: React.ElementType }[] = [
  { id: 'saved',  label: 'Saved',  icon: Bookmark },
  { id: 'recent', label: 'Recent', icon: Clock    },
  { id: 'search', label: 'Search', icon: Search   },
  { id: 'by-id',  label: 'By ID',  icon: Hash     },
]

function ArticlePicker({
  block,
  onToggle,
}: {
  block: PageBlockInput
  onToggle: (a: WireArticle) => void
}) {
  const [source, setSource] = useState<ArticleSource>('saved')
  const def         = PAGE_BLOCK_REGISTRY[block.type]
  const max         = def?.maxArticles ?? Infinity
  const selectedIds = block.content?.articleIds ?? []
  const atMax       = selectedIds.length >= max

  const props = { selectedIds, atMax, onToggle }

  return (
    <div className="flex w-64 shrink-0 flex-col overflow-hidden border-r border-line">
      {/* Source tabs */}
      <div className="shrink-0 border-b border-line">
        <div className="flex">
          {SOURCE_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setSource(id)}
              className={`flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[10px] font-semibold transition-colors ${
                source === id
                  ? 'border-b-2 border-accent-primary-solid text-accent-primary-solid'
                  : 'text-fg-secondary hover:text-fg'
              }`}
            >
              <Icon size={12} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Article count indicator */}
      <div className="shrink-0 border-b border-line px-4 py-1.5">
        <span className={`text-[10px] font-semibold ${atMax ? 'text-accent-primary-solid' : 'text-fg-secondary opacity-60'}`}>
          {selectedIds.length} / {max === Infinity ? '∞' : max} selected
          {atMax && ' — at capacity'}
        </span>
      </div>

      {/* Source content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {source === 'saved'  && <SavedSource  {...props} />}
        {source === 'recent' && <RecentSource {...props} />}
        {source === 'search' && <SearchSource {...props} />}
        {source === 'by-id'  && <ByIdSource   {...props} />}
      </div>
    </div>
  )
}

// ── Display option helpers ──────────────────────────────────────────────────────

function OptionToggle({ label, description, checked, onChange }: {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold text-fg">{label}</p>
        {description && (
          <p className="mt-0.5 text-[11px] leading-snug text-fg-secondary opacity-60">{description}</p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
          checked ? 'bg-accent-primary-solid' : 'bg-line'
        }`}
      >
        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`} />
      </button>
    </div>
  )
}

function SegmentedControl({ value, options, onChange }: {
  value: string
  options: { label: string; value: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="flex gap-1">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`flex-1 rounded border px-2 py-1 text-[11px] font-semibold transition-colors ${
            value === opt.value
              ? 'border-accent-primary-line bg-accent-primary-surface text-accent-primary-fg'
              : 'border-line bg-canvas text-fg-secondary hover:bg-fill hover:text-fg'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// ── Block fill workspace (center for articles step) ───────────────────────────

function BlockFillWorkspace({
  articleBlocks,
  currentIdx,
  articleCache,
  onPrev,
  onNext,
  onRemove,
  onConfigChange,
  parentGrid,
}: {
  articleBlocks: PageBlockInput[]
  currentIdx: number
  articleCache: Map<string, WireArticle>
  onPrev: () => void
  onNext: () => void
  onRemove: (articleId: string) => void
  onConfigChange: (key: string, value: string | number | boolean | null) => void
  parentGrid?: { layoutBlock: LayoutBlock; colKey: PageBlockColumnKey }
}) {
  const block           = articleBlocks[currentIdx]
  const def             = PAGE_BLOCK_REGISTRY[block.type]
  const selectedIds     = block.content?.articleIds ?? []
  const min             = def?.minArticles ?? 0
  const max             = def?.maxArticles ?? Infinity
  const total           = articleBlocks.length
  const filled          = selectedIds.length
  const meetsMin        = filled >= min
  const selectedArticles = selectedIds
    .map(id => articleCache.get(id))
    .filter((a): a is WireArticle => a !== undefined)

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Block navigation bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-line bg-surface px-5 py-2.5">
        <button type="button" onClick={onPrev} disabled={currentIdx === 0}
          className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-30">
          <ChevronLeft size={13} aria-hidden="true" />
          {currentIdx > 0 ? PAGE_BLOCK_REGISTRY[articleBlocks[currentIdx - 1].type]?.label : 'Back'}
        </button>

        <div className="text-center">
          <p className="text-xs font-semibold text-fg">
            {def?.label ?? block.type}
          </p>
          <p className="text-[10px] text-fg-secondary opacity-60">
            Block {currentIdx + 1} of {total}
          </p>
        </div>

        <button
          type="button"
          onClick={onNext}
          disabled={currentIdx === total - 1 || !meetsMin}
          title={!meetsMin ? `Add at least ${min} article${min !== 1 ? 's' : ''} to continue` : undefined}
          className={`flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
            meetsMin && currentIdx < total - 1
              ? 'border-accent-primary-line bg-accent-primary-surface text-accent-primary-fg hover:bg-accent-primary-fill'
              : 'border-line bg-canvas text-fg-secondary'
          }`}
        >
          {currentIdx < total - 1 ? PAGE_BLOCK_REGISTRY[articleBlocks[currentIdx + 1].type]?.label : 'Next'}
          <ChevronRight size={13} aria-hidden="true" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {/* Block info */}
        <div className="mb-4 flex items-center gap-3">
          <div className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold ${
            meetsMin ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400'
                     : 'border-line bg-fill text-fg-secondary'
          }`}>
            <FileText size={11} aria-hidden="true" />
            {filled} / {max === Infinity ? `${min}+` : fmtCapacity(min, max > UNBOUNDED_CAP ? UNBOUNDED_CAP : max, max > UNBOUNDED_CAP)} articles
            {meetsMin && <CheckCircle2 size={11} aria-hidden="true" />}
          </div>
          {def?.description && (
            <p className="text-xs text-fg-secondary">{def.description}</p>
          )}
        </div>

        {/* Articles + Display Options — side by side */}
        <div className="mb-6 grid grid-cols-2 items-start gap-5">

          {/* Left: Selected Articles */}
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-accent-tertiary-fg">Selected Articles</p>
            {selectedIds.length > 0 ? (
              <div className="space-y-2">
                {selectedIds.map((id, i) => {
                  const article = articleCache.get(id)
                  return (
                    <div key={id}
                      className="flex items-start gap-3 rounded-lg border border-line bg-surface px-3 py-2.5">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-fill text-[10px] font-bold text-fg-secondary">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12px] font-semibold text-fg">
                          {article?.title ?? id}
                        </p>
                        {article?.documentId && (
                          <p className="font-mono text-[10px] text-fg-secondary opacity-50">{article.documentId}</p>
                        )}
                      </div>
                      <button type="button" onClick={() => onRemove(id)}
                        aria-label={`Remove ${article?.title ?? id}`}
                        className="shrink-0 rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-error-fg">
                        <Trash2 size={12} aria-hidden="true" />
                      </button>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-line bg-canvas py-8 text-center">
                <BookOpen size={20} className="mb-2 text-fg-secondary opacity-20" aria-hidden="true" />
                <p className="text-xs text-fg-secondary opacity-50">
                  Select articles from the left.
                </p>
                {min > 0 && (
                  <p className="mt-1 text-[11px] text-fg-secondary opacity-40">
                    Minimum {min} required.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right: Display Options */}
          <div className="overflow-hidden rounded-lg border border-line">
            <div className="border-b border-line bg-surface px-4 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-accent-tertiary-fg">Display Options</p>
            </div>
            <div className="divide-y divide-line">

              {/* Section Header */}
              <div className="space-y-2.5 px-4 py-3">
                <OptionToggle
                  label="Section Header"
                  description="A labeled heading above this section."
                  checked={Boolean(block.config.sectionHeaderEnabled)}
                  onChange={v => onConfigChange('sectionHeaderEnabled', v)}
                />
                {Boolean(block.config.sectionHeaderEnabled) && (
                  <>
                    <input
                      type="text"
                      value={String(block.config.sectionHeaderTitle ?? '')}
                      onChange={e => onConfigChange('sectionHeaderTitle', e.target.value)}
                      placeholder="e.g. Breaking News, Analysis…"
                      className="w-full rounded border border-line bg-canvas px-3 py-1.5 text-sm text-fg placeholder:text-fg-secondary focus:border-accent-primary-solid focus:outline-none"
                    />
                    <SegmentedControl
                      value={String(block.config.sectionHeaderStyle ?? 'rule-left')}
                      options={[
                        { label: 'Rule', value: 'rule-left' },
                        { label: 'All Caps', value: 'all-caps' },
                        { label: 'Plain', value: 'plain' },
                      ]}
                      onChange={v => onConfigChange('sectionHeaderStyle', v)}
                    />
                  </>
                )}
              </div>

              {/* Show Summary — all blocks */}
              <div className="px-4 py-3">
                <OptionToggle
                  label="Show Summary"
                  description="Show article descriptions beneath each headline."
                  checked={(() => {
                    const v = block.config.showSummary
                    if (v !== undefined) return Boolean(v)
                    return ['inline-feature', 'feature-with-rail', 'hero-overlay'].includes(block.type)
                  })()}
                  onChange={v => onConfigChange('showSummary', v)}
                />
              </div>

              {/* Inline Feature: Image Side */}
              {block.type === 'inline-feature' && (
                <div className="space-y-1.5 px-4 py-3">
                  <p className="text-[12px] font-semibold text-fg">Image Side</p>
                  <SegmentedControl
                    value={String(block.config.imageSide ?? 'right')}
                    options={[
                      { label: 'Left', value: 'left' },
                      { label: 'Right', value: 'right' },
                    ]}
                    onChange={v => onConfigChange('imageSide', v)}
                  />
                </div>
              )}

              {/* Feature with Rail: Show Lead Image */}
              {block.type === 'feature-with-rail' && (
                <div className="px-4 py-3">
                  <OptionToggle
                    label="Show Lead Image"
                    checked={block.config.showImage !== false}
                    onChange={v => onConfigChange('showImage', v)}
                  />
                </div>
              )}

              {/* Hero Overlay: Overlay Intensity */}
              {block.type === 'hero-overlay' && (
                <div className="space-y-1.5 px-4 py-3">
                  <p className="text-[12px] font-semibold text-fg">Overlay Intensity</p>
                  <SegmentedControl
                    value={String(block.config.overlayIntensity ?? 'medium')}
                    options={[
                      { label: 'Low', value: 'low' },
                      { label: 'Medium', value: 'medium' },
                      { label: 'High', value: 'high' },
                    ]}
                    onChange={v => onConfigChange('overlayIntensity', v)}
                  />
                </div>
              )}

              {/* Ranked List: Numbering Style */}
              {block.type === 'ranked-list' && (
                <div className="space-y-1.5 px-4 py-3">
                  <p className="text-[12px] font-semibold text-fg">Numbering Style</p>
                  <SegmentedControl
                    value={String(block.config.numberingStyle ?? 'numeric')}
                    options={[
                      { label: 'Numeric', value: 'numeric' },
                      { label: 'Pill', value: 'pill' },
                      { label: 'Minimal', value: 'minimal' },
                    ]}
                    onChange={v => onConfigChange('numberingStyle', v)}
                  />
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Live block preview */}
        {selectedIds.length > 0 && (() => {
          const derivedConfig = deriveBlockConfig(block.type, block.config, selectedArticles.length)
          const headerEnabled = Boolean(derivedConfig.sectionHeaderEnabled) && Boolean(derivedConfig.sectionHeaderTitle)
          const headerTitle = String(derivedConfig.sectionHeaderTitle ?? '')
          const headerStyle = String(derivedConfig.sectionHeaderStyle ?? 'rule-left')
          const previewConfig = { ...derivedConfig, sectionHeaderEnabled: false }
          const blockRenderer = (
            <PageSlotRenderer
              slot={{ id: block.id, type: block.type, order: block.order, config: previewConfig }}
              articles={selectedArticles}
              slotArticleMap={{ [block.id]: selectedArticles }}
            />
          )
          const previewBox = parentGrid ? (() => {
            const preset = (parentGrid.layoutBlock.config.layoutPreset as EditorialGridLayoutPreset) ?? '3-equal'
            const presetCfg = EDITORIAL_GRID_PRESET_CONFIG[preset]
            const gridColKeys = GRID_COL_KEYS.slice(0, presetCfg.columns) as PageBlockColumnKey[]
            const showDividers = parentGrid.layoutBlock.config.showDividers !== false
            return (
              <div className="overflow-hidden rounded-lg border border-line bg-surface">
                <div
                  className={showDividers ? 'divide-x divide-line overflow-hidden' : 'overflow-hidden'}
                  style={{ display: 'grid', gridTemplateColumns: presetCfg.gridTemplate, alignItems: 'start' }}
                >
                  {gridColKeys.map(colKey => (
                    <div key={colKey} className="min-w-0 overflow-hidden">
                      {colKey === parentGrid.colKey ? (
                        <PageSlotRenderer
                          slot={{ id: block.id, type: block.type, order: block.order, config: previewConfig }}
                          articles={selectedArticles}
                          slotArticleMap={{ [block.id]: selectedArticles }}
                          compact
                          imagePadding={showDividers && colKey !== gridColKeys[gridColKeys.length - 1]}
                        />
                      ) : (
                        <div className="flex items-center justify-center py-6 opacity-20">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">Other column</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })() : (
            <div className="overflow-hidden rounded-lg border border-line bg-surface">
              {blockRenderer}
            </div>
          )
          return (
            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-accent-tertiary-fg">Block Preview</p>
              {headerEnabled && (
                headerStyle === 'all-caps' ? (
                  <header className="mb-3"><h3 className="text-xs font-bold uppercase tracking-widest text-fg-secondary">{headerTitle}</h3></header>
                ) : headerStyle === 'plain' ? (
                  <header className="mb-3"><h3 className="text-sm font-semibold text-fg">{headerTitle}</h3></header>
                ) : (
                  <header className="mb-3 border-b border-line pb-1"><h3 className="text-xs font-bold uppercase tracking-wider text-fg-secondary">{headerTitle}</h3></header>
                )
              )}
              {previewBox}
            </div>
          )
        })()}
      </div>
    </div>
  )
}

// ── Full-page preview overlay ─────────────────────────────────────────────────

function BookPreviewOverlay({ draftBlocks, articleCache, onClose, layout }: {
  draftBlocks: PageBlockInput[]
  articleCache: Map<string, WireArticle>
  onClose: () => void
  layout: Layout | null
}) {
  // Build child-to-grid mapping from the layout structure
  const childToGrid = new Map<string, { gridId: string; colKey: PageBlockColumnKey }>()
  if (layout) {
    for (const lb of layout.blocks) {
      if (lb.type !== 'editorial-grid') continue
      for (const colKey of GRID_COL_KEYS) {
        for (const cb of lb.columnBlocks?.[colKey] ?? []) {
          childToGrid.set(cb.id, { gridId: lb.id, colKey })
        }
      }
    }
  }

  // Build slotArticleMap with correct keys:
  // - standalone blocks: block.id
  // - grid children:     ${gridId}:${colKey}:${childId}  (what PageSlotRenderer expects)
  const slotArticleMap: Record<string, WireArticle[]> = {}
  for (const block of draftBlocks) {
    const ids = block.content?.articleIds ?? []
    const articles = ids.map(id => articleCache.get(id)).filter((a): a is WireArticle => a !== undefined)
    const meta = childToGrid.get(block.id)
    if (meta) {
      slotArticleMap[`${meta.gridId}:${meta.colKey}:${block.id}`] = articles
    } else {
      slotArticleMap[block.id] = articles
    }
  }

  // Build column-prefixed config overrides from child draftBlocks so grid slots
  // reflect user-set options (imageSide, showSummary, etc.) in the preview.
  const gridChildConfigsForPreview = new Map<string, Record<string, string | number | boolean | null>>()
  for (const block of draftBlocks) {
    const meta = childToGrid.get(block.id)
    if (!meta || Object.keys(block.config).length === 0) continue
    if (!gridChildConfigsForPreview.has(meta.gridId)) gridChildConfigsForPreview.set(meta.gridId, {})
    const overrides = gridChildConfigsForPreview.get(meta.gridId)!
    for (const [key, value] of Object.entries(block.config)) {
      overrides[`${meta.colKey}:${key}`] = value
    }
  }

  // Build ordered render list: grid children are collapsed into their parent shell;
  // the shell is emitted once (when the first child is encountered in draftBlocks order).
  type RenderItem = { slot: CanvasSlot; isDerivedGrid: boolean }
  const renderItems: RenderItem[] = []
  const addedGridIds = new Set<string>()
  for (const block of draftBlocks) {
    const meta = childToGrid.get(block.id)
    if (meta) {
      if (!addedGridIds.has(meta.gridId)) {
        addedGridIds.add(meta.gridId)
        const lb = layout?.blocks.find(b => b.id === meta.gridId)
        if (lb) {
          const childConfigOverrides = gridChildConfigsForPreview.get(lb.id) ?? {}
          renderItems.push({
            slot: { id: lb.id, type: lb.type, order: lb.order, config: { ...lb.config, ...childConfigOverrides }, columnBlocks: lb.columnBlocks },
            isDerivedGrid: true,
          })
        }
      }
    } else if (block.type !== 'editorial-grid') {
      const articles = slotArticleMap[block.id] ?? []
      if (articles.length > 0) {
        renderItems.push({
          slot: { id: block.id, type: block.type, order: block.order, config: block.config },
          isDerivedGrid: false,
        })
      }
    }
  }

  const hasContent = Object.values(slotArticleMap).some(a => a.length > 0)

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-canvas">
      <div className="flex shrink-0 items-center justify-between border-b border-line bg-canvas px-6 py-3">
        <div className="flex items-center gap-2">
          <Eye size={13} className="text-fg-secondary" aria-hidden="true" />
          <span className="text-sm font-semibold text-fg">Book Preview</span>
        </div>
        <div className="flex items-center gap-2">
          <DarkModeToggle className="flex h-7 w-7 items-center justify-center rounded border border-line bg-canvas text-fg transition-colors hover:bg-fill" />
          <button type="button" onClick={onClose}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg">
            <X size={12} aria-hidden="true" />
            Close
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {hasContent ? (
          <div className="mx-auto max-w-7xl space-y-10 px-6 pb-16 pt-10">
            {renderItems.map(({ slot, isDerivedGrid }) => {
              const articles = slotArticleMap[slot.id] ?? []
              const derivedConfig = isDerivedGrid
                ? slot.config
                : deriveBlockConfig(slot.type as PageBlockType, slot.config, articles.length)
              const headerEnabled = !isDerivedGrid && Boolean(derivedConfig.sectionHeaderEnabled) && Boolean(derivedConfig.sectionHeaderTitle)
              const headerTitle = String(derivedConfig.sectionHeaderTitle ?? '')
              const headerStyle = String(derivedConfig.sectionHeaderStyle ?? 'rule-left')
              const slotConfig = { ...derivedConfig, sectionHeaderEnabled: false }
              return (
                <div key={slot.id}>
                  {headerEnabled && (
                    headerStyle === 'all-caps' ? (
                      <header className="mb-3"><h3 className="text-xs font-bold uppercase tracking-widest text-fg-secondary">{headerTitle}</h3></header>
                    ) : headerStyle === 'plain' ? (
                      <header className="mb-3"><h3 className="text-sm font-semibold text-fg">{headerTitle}</h3></header>
                    ) : (
                      <header className="mb-3 border-b border-line pb-1"><h3 className="text-xs font-bold uppercase tracking-wider text-fg-secondary">{headerTitle}</h3></header>
                    )
                  )}
                  <PageSlotRenderer
                    slot={{ ...slot, config: slotConfig }}
                    articles={articles}
                    slotArticleMap={slotArticleMap}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-fg-secondary opacity-50">No articles added yet.</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Layout step components ────────────────────────────────────────────────────

function LayoutCapacityBadge({ blocks }: { blocks: LayoutBlock[] }) {
  const { totalMin, totalMax, blocks: perBlock } = computeLayoutCapacity(blocks)
  if (totalMax === 0) return null
  const anyCapped = perBlock.some(b => b.capped)
  return (
    <p className="mt-1 text-[10px] text-fg-secondary opacity-60">
      {fmtCapacity(totalMin, totalMax, anyCapped)} articles
    </p>
  )
}

function LayoutRow({ layout, selected, onClick }: { layout: Layout; selected: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-label={layout.name} aria-pressed={selected}
      className={`relative w-full overflow-hidden text-left transition-colors ${selected ? 'bg-fill' : 'hover:bg-fill/60'}`}>
      <div className={`absolute inset-y-0 left-0 w-[3px] transition-colors ${selected ? 'bg-cyan-400' : 'bg-transparent'}`} aria-hidden="true" />
      <div className="py-3 pl-5 pr-4">
        <div className="flex items-start justify-between gap-2">
          <span className={`truncate text-[12px] font-semibold leading-snug ${selected ? 'text-fg' : 'text-fg-secondary'}`}>
            {layout.name}
          </span>
          {selected && <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-cyan-500" aria-hidden="true" />}
        </div>
        {layout.description && (
          <p className="mt-0.5 line-clamp-2 text-[11px] text-fg-secondary opacity-50">{layout.description}</p>
        )}
        <LayoutCapacityBadge blocks={layout.blocks as LayoutBlock[]} />
      </div>
    </button>
  )
}

function GuidanceBanner({ selectedLayout }: { selectedLayout: Layout | null }) {
  return (
    <div className="shrink-0 border-b border-line bg-surface px-6 py-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-primary-surface">
          <BookOpen size={13} className="text-accent-primary-solid" aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm font-semibold text-fg">Choose a layout for your book</p>
          <p className="mt-0.5 max-w-prose text-xs text-fg-secondary">
            {selectedLayout ? (
              <>You&rsquo;ve selected <span className="font-semibold text-fg">{selectedLayout.name}</span>.{selectedLayout.description && <> {selectedLayout.description}</>}{' '}
                Click <strong>Next: Add Articles</strong> to continue, or choose a different layout.</>
            ) : (
              'Select a layout from the left to preview how your book will be structured. The layout determines how articles and content blocks are arranged.'
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

function CapacityBar({ layout }: { layout: Layout }) {
  const capacity = computeLayoutCapacity(layout.blocks as LayoutBlock[])
  const anyCapped = capacity.blocks.some(b => b.capped)
  if (capacity.totalMax === 0) {
    return (
      <div className="shrink-0 border-b border-line bg-canvas px-6 py-3">
        <p className="text-[11px] text-fg-secondary opacity-50">Structural-only layout — no article slots defined.</p>
      </div>
    )
  }
  return (
    <div className="shrink-0 border-b border-line bg-canvas px-6 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <FileText size={13} className="shrink-0 text-accent-primary-solid" aria-hidden="true" />
          <span className="text-xs font-semibold text-fg">
            {fmtCapacity(capacity.totalMin, capacity.totalMax, anyCapped)} articles supported
          </span>
        </div>
        <div className="hidden h-3 w-px bg-line sm:block" aria-hidden="true" />
        <div className="flex flex-wrap items-center gap-1.5">
          {capacity.blocks.map((b, i) => (
            <span key={i}
              className="rounded-full border border-line bg-fill px-2 py-0.5 text-[10px] font-medium text-fg-secondary"
              title={b.capped ? `Up to ${UNBOUNDED_CAP} shown; block supports more` : undefined}>
              {b.label}: {b.min === b.max ? b.min : b.capped ? `up to ${b.max}` : `${b.min}–${b.max}`}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Book details step ─────────────────────────────────────────────────────────

function generateSlug(title: string): string {
  const base   = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
  const suffix = crypto.randomUUID().replace(/-/g, '').slice(0, 6)
  return base ? `${base}-${suffix}` : suffix
}

function BookDetailsStep({ onSubmit, isSubmitting }: { onSubmit: (title: string, slug: string) => void; isSubmitting: boolean }) {
  const [title, setTitle] = useState('')

  const inputCls = 'w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-secondary focus:border-accent-primary-solid focus:outline-none'

  return (
    <div className="flex flex-1 items-start justify-center overflow-y-auto pt-16">
      <div className="w-full max-w-md space-y-5 px-4">
        <div>
          <div className="mb-1 flex items-center gap-1.5">
            <BookOpen size={13} className="text-accent-primary-solid" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-fg">Name Your Book</h2>
          </div>
          <p className="text-xs text-fg-secondary">Give your book a title so you can find it later.</p>
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-fg-secondary">
            Title <span className="text-error-fg">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && title.trim() && !isSubmitting) onSubmit(title.trim(), generateSlug(title.trim())) }}
            placeholder="e.g. Russia Policy Overview Q2 2026"
            className={inputCls}
            autoFocus
          />
        </div>

        <button
          type="button"
          onClick={() => onSubmit(title.trim(), generateSlug(title.trim()))}
          disabled={!title.trim() || isSubmitting}
          className="flex w-full items-center justify-center gap-2 rounded bg-accent-primary-solid py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSubmitting && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
          Create Book
        </button>
      </div>
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const GRID_COL_KEYS = ['column1', 'column2', 'column3'] as const

function initDraftBlocks(layout: Layout): PageBlockInput[] {
  const result: PageBlockInput[] = []
  for (const b of layout.blocks) {
    if (b.type === 'editorial-grid') {
      // Grid shell — holds no articles itself; column children are filled individually
      result.push({ id: b.id, type: b.type, order: b.order, config: { ...b.config } })
      // Flatten each column child as its own fillable block
      for (const colKey of GRID_COL_KEYS) {
        for (const cb of b.columnBlocks?.[colKey] ?? []) {
          result.push({
            id: cb.id,
            type: cb.type,
            order: result.length,
            config: {},
            content: { source: 'static' as const, articleIds: [] },
          })
        }
      }
    } else {
      result.push({
        id: b.id,
        type: b.type,
        order: b.order,
        config: { ...b.config },
        content: { source: 'static' as const, articleIds: [] },
      })
    }
  }
  return result
}

// ── Main page ─────────────────────────────────────────────────────────────────

function CreateBookPage() {
  const navigate = useNavigate()
  const { mutate: createPage, isPending: isCreating } = useCreatePage()

  // ── Layout step state ──
  const { data: layouts, isLoading: layoutsLoading, isError: layoutsError } = useLayouts({ usageContext: 'book' })
  const [layoutSearch, setLayoutSearch] = useState('')
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null)

  const layoutQuery    = layoutSearch.trim().toLowerCase()
  const allLayouts     = layouts ?? []
  const filteredLayouts = allLayouts.filter(
    l => !layoutQuery || l.name.toLowerCase().includes(layoutQuery) || (l.description ?? '').toLowerCase().includes(layoutQuery)
  )
  const selectedLayout = allLayouts.find(l => l.id === selectedLayoutId) ?? null

  // ── Articles step state ──
  const [draftBlocks, setDraftBlocks]       = useState<PageBlockInput[]>([])
  const [currentBlockIdx, setCurrentBlockIdx] = useState(0)
  const [articleCache, setArticleCache]     = useState<Map<string, WireArticle>>(new Map())

  const articleBlocks = draftBlocks.filter(b => {
    const def = PAGE_BLOCK_REGISTRY[b.type]
    return def && def.maxArticles > 0
  })

  const allBlocksMeetMin = articleBlocks.every(b => {
    const def = PAGE_BLOCK_REGISTRY[b.type]
    return (b.content?.articleIds ?? []).length >= (def?.minArticles ?? 0)
  })

  // For the current article block, find its parent editorial-grid (if any) so the
  // preview can render the block inside the correct column layout.
  const currentArticleBlock = articleBlocks[currentBlockIdx]
  const parentGridForCurrentBlock = (() => {
    if (!selectedLayout || !currentArticleBlock) return undefined
    for (const lb of selectedLayout.blocks) {
      if (lb.type !== 'editorial-grid') continue
      for (const colKey of GRID_COL_KEYS) {
        if (lb.columnBlocks?.[colKey]?.some(cb => cb.id === currentArticleBlock.id)) {
          return { layoutBlock: lb, colKey: colKey as PageBlockColumnKey }
        }
      }
    }
    return undefined
  })()

  // ── Wizard step ──
  const [step, setStep] = useState<WizardStep>('layout')
  const [showPreview, setShowPreview] = useState(false)

  // ── Navigation ──
  function goToLayout() { setStep('layout') }

  function goToArticles() {
    if (!selectedLayout) return
    setDraftBlocks(initDraftBlocks(selectedLayout))
    setCurrentBlockIdx(0)
    setStep('articles')
  }

  function goToDetails() { setStep('details') }

  function handleCancel() { navigate('/wire/me') }

  // ── Article toggle ──
  function cacheArticles(articles: WireArticle[]) {
    setArticleCache(prev => {
      const next = new Map(prev)
      articles.forEach(a => next.set(a.id, a))
      return next
    })
  }

  function toggleArticle(a: WireArticle) {
    const block = articleBlocks[currentBlockIdx]
    if (!block) return

    cacheArticles([a])

    setDraftBlocks(prev => prev.map(b => {
      if (b.id !== block.id) return b
      const def  = PAGE_BLOCK_REGISTRY[b.type]
      const max  = def?.maxArticles ?? Infinity
      const ids  = b.content?.articleIds ?? []
      const newIds = ids.includes(a.id)
        ? ids.filter(id => id !== a.id)
        : ids.length < max ? [...ids, a.id] : ids
      return { ...b, content: { source: 'static' as const, articleIds: newIds } }
    }))
  }

  function removeArticle(articleId: string) {
    const block = articleBlocks[currentBlockIdx]
    if (!block) return
    setDraftBlocks(prev => prev.map(b => {
      if (b.id !== block.id) return b
      const ids = (b.content?.articleIds ?? []).filter(id => id !== articleId)
      return { ...b, content: { source: 'static' as const, articleIds: ids } }
    }))
  }

  function updateBlockConfig(blockId: string, key: string, value: string | number | boolean | null) {
    setDraftBlocks(prev => prev.map(b => {
      if (b.id !== blockId) return b
      return { ...b, config: { ...b.config, [key]: value } }
    }))
  }

  // ── Page creation ──
  function handleCreateBook(title: string, slug: string) {
    if (!selectedLayout) return

    // Build a map from child-block ID → { gridId, colKey } using the original layout structure.
    // This lets us re-fold the individually-filled children back into columnContent for the API.
    const childToGrid = new Map<string, { gridId: string; colKey: PageBlockColumnKey }>()
    for (const lb of selectedLayout.blocks) {
      if (lb.type !== 'editorial-grid') continue
      for (const colKey of GRID_COL_KEYS) {
        for (const cb of lb.columnBlocks?.[colKey] ?? []) {
          childToGrid.set(cb.id, { gridId: lb.id, colKey })
        }
      }
    }

    // Accumulate per-column content for each grid block.
    // Also collect child configs to merge into the grid's config as `${colKey}:${key}` entries,
    // which is the convention PageSlotRenderer uses when rendering editorial-grid columns.
    const gridColumnContent = new Map<string, Partial<Record<PageBlockColumnKey, ColumnBlock[]>>>()
    const gridChildConfigs  = new Map<string, Record<string, string | number | boolean | null>>()
    for (const b of draftBlocks) {
      const meta = childToGrid.get(b.id)
      if (!meta) continue
      if (!gridColumnContent.has(meta.gridId)) gridColumnContent.set(meta.gridId, {})
      const cols = gridColumnContent.get(meta.gridId)!
      if (!cols[meta.colKey]) cols[meta.colKey] = []
      cols[meta.colKey]!.push({ id: b.id, type: b.type, content: b.content })

      // Propagate child config keys onto the grid config with column prefix
      if (Object.keys(b.config).length > 0) {
        if (!gridChildConfigs.has(meta.gridId)) gridChildConfigs.set(meta.gridId, {})
        const overrides = gridChildConfigs.get(meta.gridId)!
        for (const [key, value] of Object.entries(b.config)) {
          overrides[`${meta.colKey}:${key}`] = value
        }
      }
    }

    // Build final API blocks: skip raw grid children (they live inside columnContent)
    const apiBlocks: PageBlockInput[] = draftBlocks
      .filter(b => !childToGrid.has(b.id))
      .map(b => {
        const derived = deriveBlockConfig(b.type, b.config, (b.content?.articleIds ?? []).length)
        if (b.type === 'editorial-grid') {
          const childConfigOverrides = gridChildConfigs.get(b.id) ?? {}
          return { ...b, config: { ...derived, ...childConfigOverrides }, columnContent: gridColumnContent.get(b.id) ?? {} }
        }
        return { ...b, config: derived }
      })

    createPage(
      { kind: 'book', title, slug, originLayoutId: selectedLayout.id, blocks: apiBlocks },
      {
        onSuccess: (page) => {
          toast.success('Book created', { description: `"${title}" is ready.` })
          navigate(`/wire/books/${page.id}`)
        },
        onError: () => toast.error('Failed to create book. Please try again.'),
      }
    )
  }

  // ── Render ──
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-canvas">

      {/* ── Wizard bar ── */}
      {step === 'layout' && (
        <WizardBar
          step="layout"
          nextLabel="Next: Add Articles"
          nextDisabled={!selectedLayout}
          onCancel={handleCancel}
          onNext={goToArticles}
        />
      )}
      {step === 'articles' && (
        <WizardBar
          step="articles"
          nextLabel="Next: Book Details"
          nextDisabled={!allBlocksMeetMin}
          onBack={goToLayout}
          onCancel={handleCancel}
          onNext={goToDetails}
          onPreview={() => setShowPreview(true)}
        />
      )}
      {step === 'details' && (
        <WizardBar
          step="details"
          nextLabel="Creating…"
          nextDisabled={true}
          onBack={goToArticles}
          onCancel={handleCancel}
          onNext={() => {}}
          nextLoading={false}
          onPreview={() => setShowPreview(true)}
        />
      )}

      {showPreview && (
        <BookPreviewOverlay
          draftBlocks={draftBlocks}
          articleCache={articleCache}
          onClose={() => setShowPreview(false)}
          layout={selectedLayout}
        />
      )}

      {/* ── Step: Choose Layout ── */}
      {step === 'layout' && (
        <div className="flex flex-1 overflow-hidden">
          {/* Left shelf */}
          <div className="flex w-64 shrink-0 flex-col overflow-hidden border-r border-line">
            <div className="shrink-0 border-b border-line px-3 py-2.5">
              <div className="relative">
                <Search size={12} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-secondary" aria-hidden="true" />
                <input type="search" placeholder="Filter layouts…" value={layoutSearch}
                  onChange={e => setLayoutSearch(e.target.value)}
                  className="h-7 w-full rounded border border-line bg-canvas pl-7 pr-3 text-xs text-fg placeholder:text-fg-secondary focus:outline-none focus:ring-1 focus:ring-line-strong" />
              </div>
            </div>
            {!layoutsLoading && !layoutsError && allLayouts.length > 0 && (
              <div className="shrink-0 border-b border-line px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-fg-secondary opacity-50">
                {filteredLayouts.length} layout{filteredLayouts.length !== 1 ? 's' : ''}
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {layoutsLoading && (
                <div className="flex items-center justify-center gap-2 py-10 text-fg-secondary">
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                </div>
              )}
              {layoutsError && <p className="px-4 py-4 text-xs text-error-fg">Failed to load layouts. Please refresh.</p>}
              {!layoutsLoading && !layoutsError && filteredLayouts.length === 0 && (
                <div className="flex flex-col items-center px-4 py-12 text-center">
                  <LayoutTemplate size={20} className="mb-2 text-fg-secondary opacity-25" aria-hidden="true" />
                  <p className="text-xs text-fg-secondary opacity-60">
                    {layoutQuery ? 'No layouts match your search.' : 'No book layouts are available yet.'}
                  </p>
                </div>
              )}
              {!layoutsLoading && !layoutsError && filteredLayouts.map(layout => (
                <LayoutRow key={layout.id} layout={layout} selected={selectedLayoutId === layout.id}
                  onClick={() => setSelectedLayoutId(layout.id)} />
              ))}
            </div>
          </div>

          {/* Center */}
          <div className="flex flex-1 flex-col overflow-hidden">
            <GuidanceBanner selectedLayout={selectedLayout} />
            {selectedLayout && <CapacityBar layout={selectedLayout} />}
            {selectedLayout ? (
              <div className="flex-1 overflow-y-auto">
                <LayoutPreview slots={selectedLayout.blocks as CanvasSlot[]} mode="inline" />
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <div className="pointer-events-none select-none text-center">
                  <LayoutTemplate size={48} className="mx-auto mb-3 text-fg-secondary opacity-10" aria-hidden="true" />
                  <p className="text-sm text-fg-secondary opacity-40">Select a layout to preview it here</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Step: Add Articles ── */}
      {step === 'articles' && articleBlocks.length > 0 && (
        <div className="flex flex-1 overflow-hidden">
          <ArticlePicker
            block={articleBlocks[currentBlockIdx]}
            onToggle={toggleArticle}
          />
          <BlockFillWorkspace
            articleBlocks={articleBlocks}
            currentIdx={currentBlockIdx}
            articleCache={articleCache}
            onPrev={() => setCurrentBlockIdx(i => Math.max(0, i - 1))}
            onNext={() => setCurrentBlockIdx(i => Math.min(articleBlocks.length - 1, i + 1))}
            onRemove={removeArticle}
            onConfigChange={(key, value) => updateBlockConfig(articleBlocks[currentBlockIdx].id, key, value)}
            parentGrid={parentGridForCurrentBlock}
          />
        </div>
      )}

      {/* ── Step: Add Articles (layout has no article blocks) ── */}
      {step === 'articles' && articleBlocks.length === 0 && (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-fg-secondary opacity-50">This layout has no article slots.</p>
        </div>
      )}

      {/* ── Step: Book Details ── */}
      {step === 'details' && (
        <BookDetailsStep
          onSubmit={handleCreateBook}
          isSubmitting={isCreating}
        />
      )}
    </div>
  )
}

export default CreateBookPage
